import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import NumberInput from '~/components/Inputs/NumberInput';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import {
    countClearedSlicesWithContent,
    createDeletedPreviewTexture,
} from '~/engine/data/structure/deleteScopePlan';
import {
    buildDeleteScopePlan,
    useClearContourExtent,
} from '~/hooks/structure-set/useClearContourExtent';
import { useRoiSdfOperation } from '~/hooks/structure-set/useRoiSdfOperation';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import {
    keepEveryNthSliceValueState,
    minSliceInterval,
} from '~/state/atoms/toolsAtoms';
import OperationApplyButton from '~/components/toolbar/OperationsToolbar/OperationApplyButton';
import ShowPreviewToggle from '~/components/toolbar/OperationsToolbar/ShowPreviewToggle';
import PreviewResultStatus from '~/components/toolbar/OperationsToolbar/PreviewResultStatus';
import { AUTO_PREVIEW_DELAY_MS } from '~/components/toolbar/OperationsToolbar/autoPreview';
import { usePostProcessingPreview } from '~/hooks/structure-set/usePostProcessingPreview';
import { useReportBusy } from '~/components/toolbar/OperationsToolbar/useReportBusy';

/**
 * The thinning's wording. A module constant so the hook's handler keeps
 * one identity across renders.
 */
const THIN_MESSAGES = {
    successMessageId: 'postProcessing.delete.success',
    failedMessageId: 'postProcessing.delete.failed',
    undoLabelId: 'contourDeletion.undo',
    undoUnavailableMessageId: 'contourDeletion.undoUnavailable',
    logLabel: 'Thin contours',
} as const;

// ── Thin tab content ─────────────────────────────────────────────────

/**
 * Thins a structure to a retained cadence: every slice but every N-th
 * goes, and the first and last slice holding contour are kept so that
 * thinning never shortens a structure's extent.
 *
 * This is the one deletion that stays in the panel, and the one that
 * keeps preview-then-apply. The others moved to the viewport, where they
 * commit on the click and their report carries the way back — which is
 * the right trade for a deletion whose result the user can predict.
 * Nobody can predict which slices a cadence leaves standing on a
 * structure that skips slices, so this one is shown before it is applied.
 *
 * No approval check here: the panel's entry ToolbarButton is already
 * disabled for an approved ROI, and PostProcessingSection's auto-close
 * effect closes this panel if that becomes true while it is open — a
 * second check here would be a divergent source of truth for the same
 * rule. `RegionOfInterest._ensureCanModify` remains the backstop.
 */
export default function ThinTab({
    onPreventCloseChange,
}: {
    onPreventCloseChange: (prevent: boolean) => void;
}) {
    const { environment, roiNumber: selectedROI } = useActiveEditTarget();
    // Session state rather than tab state, so the cadence a clinician
    // chose survives the panel being closed and reopened between
    // structures.
    const [nthValue, setNthValue] = useRecoilState(keepEveryNthSliceValueState);
    const { selectedStructureSet, isTilted } = useStructureToolGating();

    const { isProcessing } = useRoiSdfOperation();
    const {
        isPreviewing,
        isPreviewReady,
        isPreviewVisible,
        setPreviewVisible,
        preview,
        clearPreview,
        invalidatePreview,
        sdfRevision,
    } = usePostProcessingPreview();
    const { clearContourExtent, isClearing } = useClearContourExtent(
        selectedStructureSet?.dicomUid,
        THIN_MESSAGES
    );
    // True once a preview has run and found that the plan touches no slice
    // holding content. Without it an empty diff overlay — nothing drawn —
    // is indistinguishable from a preview that never rendered.
    const [previewFoundNothing, setPreviewFoundNothing] = useState(false);

    const isBusy = isProcessing || isClearing;
    useReportBusy(isBusy, onPreventCloseChange);

    // Below the clinical minimum there is no cadence to delete and
    // keepEveryNthSlice would throw rather than run.
    //
    // Tilt refuses it outright. A gantry-tilted stack is sheared and
    // {@link Grid} carries a rigid rotation, so the ROI is sampled on a
    // grid whose slices are not the acquisition slices: thinning by grid
    // slice index would leave a cadence that is not a cadence of image
    // slices, and a structure the user cannot predict from what is on
    // screen. (The whole-structure and single-slice deletions do not
    // share this — they moved to the viewport, and neither thins by
    // index.)
    const cadenceUnavailable = nthValue < minSliceInterval || isTilted;

    // "A result preview for the CURRENT settings is on screen." Read both
    // by the line that tells the user what they are looking at and by
    // Apply, which commits what that preview shows.
    const isPreviewOnScreen = isPreviewReady && !cadenceUnavailable;

    const runPreview = useCallback(() => {
        setPreviewFoundNothing(false);
        preview(
            async (srcTexture, signal) => {
                // The viewed slice is not an input: a cadence is planned
                // over the whole volume, and the displayed slice is only
                // the viewpoint its result is re-sliced at. Scrolling
                // therefore does not invalidate this preview.
                const plan = buildDeleteScopePlan(
                    'keep-nth',
                    srcTexture,
                    environment?.image,
                    null,
                    nthValue
                );
                if (!plan) {
                    return { status: 'failed' };
                }
                // Gated on the signal: a superseded run must not state its
                // outcome over a newer run's overlay.
                if (!signal.aborted) {
                    setPreviewFoundNothing(
                        countClearedSlicesWithContent(
                            srcTexture.signedDistanceField,
                            plan
                        ) === 0
                    );
                }
                // A cadence that reaches no slice holding contour still
                // has a result to show — the structure unchanged — so it
                // is 'unchanged' rather than a failure, which is what
                // unlocks Apply on it.
                const texture = createDeletedPreviewTexture(srcTexture, plan);
                return texture
                    ? { status: 'changed', texture }
                    : { status: 'unchanged' };
            },
            `keep-nth:${nthValue}`,
            { hidePreviewedStructure: true }
        );
    }, [preview, environment, nthValue]);

    // The preview computes itself on every input that moves the operation
    // out from under the displayed result: the N value, the environment
    // the plan resolves against, the selected ROI, and sdfRevision, for an
    // ROI edited under us — including by a deletion taken from the
    // viewport while this panel is open. Debounced, because a cadence is
    // typed and would otherwise queue a GPU compute per digit.
    //
    // invalidatePreview, not clearPreview: readiness drops SYNCHRONOUSLY
    // here, so Apply cannot commit the previous cadence's result during
    // the debounce, while the picture stays up until the recompute swaps
    // it in rather than flickering to nothing.
    //
    // Skipped during an apply so the commit is not disturbed, and torn
    // down entirely where the cadence is refused, so a stale result cannot
    // be mistaken for the current state.
    useEffect(() => {
        if (isBusy) {
            return;
        }
        if (cadenceUnavailable) {
            clearPreview();
            setPreviewFoundNothing(false);
            return;
        }
        invalidatePreview();
        setPreviewFoundNothing(false);
        const timeout = setTimeout(() => runPreview(), AUTO_PREVIEW_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [
        nthValue,
        environment,
        selectedROI,
        isBusy,
        cadenceUnavailable,
        sdfRevision,
        runPreview,
        clearPreview,
        invalidatePreview,
    ]);

    const handleApply = async () => {
        if (!environment || selectedROI === -1 || cadenceUnavailable) {
            return;
        }
        clearPreview();
        setPreviewFoundNothing(false);
        // No Undo on the report: the user applied a result they had just
        // seen previewed, so the panel's own undo control is the way back
        // rather than a transient offer on a toast.
        await clearContourExtent({
            scope: 'keep-nth',
            nthValue,
            offerUndo: false,
        });
    };

    const handleNthValueChange = (value: number | null) => {
        // NumberInput reports null for a cleared field; the cadence keeps
        // its last value rather than falling to a number nobody chose.
        if (value !== null) {
            setNthValue(value);
        }
    };

    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
                <FormattedMessage id="postProcessing.thin.description" />
            </Typography>
            <NumberInput
                value={nthValue}
                onChange={handleNthValueChange}
                min={minSliceInterval}
                step={1}
                precision={0}
                // The dock that holds the tab scrolls only on a window too
                // short for it, so the wheel seldom has a scroll to take
                // from the list there.
                stepOnWheel
                fullWidth
                data-testid="post-processing-thin-nth"
            />
            {/* Stated where the disabled buttons are, not only in a
                tooltip: a greyed-out Apply with no reason beside it is a
                capability the user cannot tell from a bug. */}
            {isTilted && (
                <Typography variant="body2" color="text.secondary">
                    <FormattedMessage id="postProcessing.delete.obliqueKeepNth" />
                </Typography>
            )}
            <ShowPreviewToggle
                visible={isPreviewVisible}
                onChange={setPreviewVisible}
                disabled={!isPreviewReady}
            />
            <OperationApplyButton
                onApply={() => void handleApply()}
                isPreviewing={isPreviewing}
                isPreviewReady={isPreviewOnScreen}
                isProcessing={isBusy}
                applyDisabled={cadenceUnavailable}
            />
            {/* The same two sentences the other operation tabs say for the
                same two states: an emptied viewport under a result preview
                is indistinguishable from an overlay that never drew, so
                which of the two it is is stated in words. */}
            <PreviewResultStatus
                isPreviewOnScreen={isPreviewOnScreen}
                previewChangesNothing={previewFoundNothing}
            />
        </Box>
    );
}
